import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add, 
  Edit, 
  Delete,
  PlayArrow,
  Stop,
  AccessTime,
  TrendingUp,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { galiAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const StatCard = ({ title, value, icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Card
      sx={{
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 3,
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ color, fontWeight: 'bold' }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const GaliMarkets = () => {
  const { user } = useAuth();
  const [markets, setMarkets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    running: 0
  });
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [formData, setFormData] = useState({
    market_name: '',
    open_time: '09:00',
    close_time: '10:00',
    status: 'active'
  });

  useEffect(() => {
    fetchMarkets();
  }, []);

  const fetchMarkets = async () => {
    try {
      setLoading(true);
      const response = await galiAPI.getGaliMarkets();
      setMarkets(response.data.markets);
      setStats(response.data.stats);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch markets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMarket = async () => {
    try {
      await galiAPI.createGaliMarket(formData);
      toast.success('Market created successfully');
      setCreateDialog(false);
      setFormData({
        market_name: '',
        open_time: '09:00',
        close_time: '10:00',
        status: 'active'
      });
      fetchMarkets();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create market');
    }
  };

  const handleEditMarket = async () => {
    try {
      await galiAPI.updateGaliMarket(selectedMarket.id, formData);
      toast.success('Market updated successfully');
      setEditDialog(false);
      setSelectedMarket(null);
      fetchMarkets();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update market');
    }
  };

  const handleDeleteMarket = async (id) => {
    if (window.confirm('Are you sure you want to delete this market?')) {
      try {
        await galiAPI.deleteGaliMarket(id);
        toast.success('Market deleted successfully');
        fetchMarkets();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete market');
      }
    }
  };

  const openEditDialog = (market) => {
    setSelectedMarket(market);
    setFormData({
      market_name: market.market_name,
      open_time: market.open_time,
      close_time: market.close_time,
      status: market.status
    });
    setEditDialog(true);
  };

  const getMarketStatus = (market) => {
    const now = new Date();
    const currentTime = now.toTimeString().split(' ')[0];
    
    if (market.status !== 'active') return { label: 'Inactive', color: 'error' };
    if (currentTime >= market.open_time && currentTime <= market.close_time) {
      return { label: 'Running', color: 'success' };
    }
    return { label: 'Closed', color: 'warning' };
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'market_name', 
      headerName: 'Market Name', 
      width: 200, 
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'timing',
      headerName: 'Timing',
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <AccessTime sx={{ mr: 1, fontSize: 16 }} />
          <Typography variant="body2">
            {params.row.open_time} - {params.row.close_time}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'market_status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = getMarketStatus(params.row);
        return <Chip label={status.label} color={status.color} variant="filled" size="small" />;
      },
    },
    {
      field: 'status',
      headerName: 'Market Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value?.toUpperCase()}
          color={params.value === 'active' ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 120,
      renderCell: (params) => new Date(params.value).toLocaleDateString()
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton 
              size="small" 
              onClick={() => openEditDialog(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          {user.user_type === 'owner' && (
            <Tooltip title="Delete">
              <IconButton 
                size="small" 
                onClick={() => handleDeleteMarket(params.row.id)}
                color="error"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Gali Markets
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all Gali markets and their timings
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialog(true)}
          sx={{ borderRadius: 2 }}
        >
          Add New Market
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Markets"
            value={stats.total}
            icon={<TrendingUp sx={{ fontSize: 40 }} />}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Active Markets"
            value={stats.active}
            icon={<PlayArrow sx={{ fontSize: 40 }} />}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Running Now"
            value={stats.running}
            icon={<AccessTime sx={{ fontSize: 40 }} />}
            color="#f59e0b"
          />
        </Grid>
      </Grid>

      {/* Markets Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Markets ({markets.length})
          </Typography>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={markets}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Create Market Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Gali Market</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Market Name"
                value={formData.market_name}
                onChange={(e) => setFormData({...formData, market_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Open Time"
                type="time"
                value={formData.open_time}
                onChange={(e) => setFormData({...formData, open_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Close Time"
                type="time"
                value={formData.close_time}
                onChange={(e) => setFormData({...formData, close_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateMarket} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Market Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Gali Market</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Market Name"
                value={formData.market_name}
                onChange={(e) => setFormData({...formData, market_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Open Time"
                type="time"
                value={formData.open_time}
                onChange={(e) => setFormData({...formData, open_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Close Time"
                type="time"
                value={formData.close_time}
                onChange={(e) => setFormData({...formData, close_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditMarket} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default GaliMarkets;
